import { Capacitor } from "@capacitor/core";
import { KeyboardHandler } from "../utils/keyboardHandler";

import React, { useState, useRef, useEffect } from "react";
import { crashlytics } from "@/services/firebase";

interface ReportUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  reportedUsername: string;
  reporterEmail?: string;
  reporterUsername?: string;
}

const ReportUserModal: React.FC<ReportUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  reportedUsername,
  reporterUsername,
  reporterEmail,
}) => {
  const reasonTemplateHtml =
    '<div><span style="font-weight: 500;">Please be elaborate and include all details to consider.</span></div>' +
    '<div style="height:8px;"></div>' +
    '<div><b>Reason for report:</b> <span class="template-placeholder" style="color: #888;">[Please describe the reason for your report]</span></div>' +
    '<div><b>Related activities (IDs or titles):</b> <span class="template-placeholder" style="color: #888;">[List any specific activities you have issues with]</span></div>' +
    '<div><b>Related chats (IDs or usernames):</b> <span class="template-placeholder" style="color: #888;">[List any specific chats or users involved]</span></div>' +
    '<div><b>Events or dates to report:</b> <span class="template-placeholder" style="color: #888;">[Provide any relevant events or dates]</span></div>' +
    '<div><b>Additional details:</b> <span class="template-placeholder" style="color: #888;">[Any other information]</span></div>';
  const [reason, setReason] = useState("");
  const templateShownRef = useRef(true);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [modalHeight, setModalHeight] = useState<string | undefined>(undefined);
  // For iOS, track visual viewport height
  const [visualViewportHeight, setVisualViewportHeight] = useState<
    number | null
  >(null);

  // Keyboard height state for native platforms
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Listen for keyboard height using KeyboardHandler (native platforms only)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const cb = (isOpen: boolean, height: number) => {
      setKeyboardHeight(isOpen ? height : 0);
    };
    KeyboardHandler.getInstance().initialize();
    KeyboardHandler.getInstance().onKeyboardChange(cb);
    // Listen for visualViewport changes (iOS)
    if (window.visualViewport) {
      const handleResize = () =>
        setVisualViewportHeight(window.visualViewport!.height);
      window.visualViewport.addEventListener("resize", handleResize);
      setVisualViewportHeight(window.visualViewport.height);
      return () => {
        window.visualViewport.removeEventListener("resize", handleResize);
        KeyboardHandler.getInstance().offKeyboardChange(cb);
      };
    }
    return () => {
      KeyboardHandler.getInstance().offKeyboardChange(cb);
    };
  }, []);

  // Dynamically adjust modal height for keyboard (native platforms and iOS)
  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();
    const isIOS =
      typeof navigator !== "undefined" &&
      /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isNative) {
      setModalHeight("90%");
      return;
    }
    if (Capacitor.getPlatform() === "android") {
      setModalHeight("90%"); // resizeOnFullScreen is handled in capacitor.config.ts
      return;
    }
    if (isIOS) {
      if (isInputFocused && visualViewportHeight) {
        const height =
          keyboardHeight > 0
            ? visualViewportHeight - keyboardHeight
            : visualViewportHeight;
        setModalHeight(height + "px");
      } else {
        setModalHeight(
          "calc(90vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 40px)",
        ); // 40px for approx. 90% height with some margin
      }
      return;
    }
    setModalHeight("90%");
  }, [isInputFocused, keyboardHeight, visualViewportHeight]);

  // Set the template only on initial mount or when modal is reopened
  useEffect(() => {
    if (isOpen) {
      templateShownRef.current = true;
      setReason("");
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.innerHTML = reasonTemplateHtml;
        }
      }, 0);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!reason.trim() || reason.length < 10) {
      setError("Please provide a detailed reason (at least 10 characters).");
      return;
    }
    setLoading(true);
    try {
      await onSubmit(reason);
      setSuccess(true);
    } catch (e) {
      crashlytics.logError(
        `Error submitting user report. Error: ${JSON.stringify(e?.message)}`,
        "ReportUserModal.tsx",
        e,
      );
      setError("Failed to submit report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [fadeOut, setFadeOut] = useState(false);

  // Fade out when closing
  useEffect(() => {
    if (!isOpen && fadeOut) {
      const timer = setTimeout(() => setFadeOut(false), 250);
      return () => clearTimeout(timer);
    }
    if (isOpen) setFadeOut(true);
  }, [isOpen, fadeOut]);

  if (!isOpen && !fadeOut) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 p-2 pt-20 transition-opacity duration-250 ${isOpen ? "opacity-100" : "opacity-0"}`}
      style={{ pointerEvents: isOpen ? "auto" : "none" }}
    >
      <div
        className={
          "bg-white rounded-2xl shadow-2xl max-w-xl w-full relative flex flex-col overflow-y-scroll min-h-0"
        }
        style={{
          maxHeight: modalHeight || "90%",
          WebkitOverflowScrolling: "touch",
          touchAction: "manipulation",
        }}
      >
        <div className="p-4 flex-grow flex flex-col">
          <button
            className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-3xl"
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
          <div
            className="overflow-y-auto outline-none p-1"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <h2 className="text-2xl font-bold mb-4">
              Report {reportedUsername}
            </h2>
            <div className="mb-4 text-sm text-gray-600">
              If you need to get in touch separately, you can{" "}
              <a
                href="#"
                className="text-blue-600 underline"
                onClick={(e) => {
                  e.preventDefault();
                  const subject = encodeURIComponent(
                    `User Report: Reporting ${reportedUsername} - Reporter ${reporterUsername || ""}`,
                  );
                  const body = encodeURIComponent(
                    `Hi Who's In? Support Team,\n\nThis is ${reporterUsername || "the reporter"}. I would like to get in touch regarding a user report.\n\nReported User: ${reportedUsername}\nReporter Username: ${reporterUsername || ""}\nReporter Email: ${reporterEmail || ""}\n\nReason for report: [Please describe the reason for your report]\n\nRelated activities (IDs or titles): [List any specific activities you have issues with]\n\nRelated chats (IDs or usernames): [List any specific chats or users involved]\n\nEvents or dates to report: [Provide any relevant events or dates]\n\nAdditional details: [Any other information]\n\nImportant: We take all reports very seriously. Please only report users for genuine reasons. False or malicious reports are also considered a serious violation and may result in action against your account.\n\nI agree.\n\nBest regards,\n${reporterUsername || ""}`,
                  );
                  let mailto = `mailto:contact@whoisin.sg?subject=${subject}&body=${body}`;
                  if (reporterEmail) {
                    mailto += `&cc=${encodeURIComponent(reporterEmail)}`;
                  }
                  window.location.href = mailto;
                }}
              >
                contact us
              </a>
              .
            </div>
            {success ? (
              <div className="text-green-600 font-semibold text-center py-10 text-lg">
                Thank you for your report. Our team will review it soon.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col">
                <label className="block mb-2 font-medium text-base">
                  Reason <span className="text-red-500">*</span>
                </label>
                <div
                  className="w-full border rounded-lg p-3 min-h-[120px] max-h-[200px] mb-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white overflow-y-auto"
                  contentEditable={!loading}
                  suppressContentEditableWarning={true}
                  style={{
                    whiteSpace: "pre-wrap",
                    outline: "none",
                    WebkitOverflowScrolling: "touch",
                  }}
                  ref={(el) => {
                    inputRef.current = el;
                  }}
                  onFocus={(e) => {
                    setIsInputFocused(true);
                    // Remove all template placeholders on first focus
                    if (templateShownRef.current) {
                      const el = e.currentTarget;
                      // Remove all spans with class 'template-placeholder'
                      el.querySelectorAll(".template-placeholder").forEach(
                        (node) => node.remove(),
                      );
                      // Remove any leftover brackets or placeholder text
                      el.innerHTML = el.innerHTML.replace(/\[.*?\]/g, "");
                      templateShownRef.current = false;
                    }
                    // Scroll input into view for iOS keyboard
                    setTimeout(() => {
                      if (e.currentTarget) {
                        e.currentTarget.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                      }
                    }, 200);
                  }}
                  onBlur={() => setIsInputFocused(false)}
                  onInput={(e) => {
                    const text = e.currentTarget.innerText;
                    setReason(text);
                  }}
                  aria-label="Reason for report"
                  role="textbox"
                />
                {error && (
                  <div className="text-red-500 text-sm mb-2">{error}</div>
                )}
                <button
                  type="submit"
                  className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-60 text-base w-full mt-2"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit Report"}
                </button>
              </form>
            )}
          </div>
          <div className="mt-4 mr-5 p-2 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 rounded">
            <strong>Important:</strong> We take all reports very seriously.
            Please only report users for genuine reasons. False or malicious
            reports are also considered a serious violation and may result in
            action against your account.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportUserModal;
